/**
 * "Nieuw bericht" bovenaan de lijst van Vandaag (sessie 72, punt 10).
 *
 * Klant zoeken op naam, e-mail of nummer (dezelfde zoekroute als de
 * klantpagina), dan het kanaal kiezen: E-mail of WhatsApp. Dat zet een zaak
 * klaar zoals een inkomende en opent hem met het antwoordvak in die stand;
 * Sanne of Nova schrijven daar zoals in elke zaak. Er gaat niets weg.
 * Staat een kanaal uit (geen adres, geen mobiel nummer), dan zegt de knop
 * waarom.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { v6Api } from "../api";
import { Icoon } from "../Icoon";
import { useNieuwBericht, type NieuwKanaal } from "../nieuwBericht";
import "./nieuwbericht.css";

const MIN_ZOEK = 2;

/** Wacht even met zoeken tot JP stopt met typen. */
function useVertraagd(waarde: string, ms = 250): string {
  const [vertraagd, setVertraagd] = useState(waarde);
  useEffect(() => {
    const t = window.setTimeout(() => setVertraagd(waarde), ms);
    return () => window.clearTimeout(t);
  }, [waarde, ms]);
  return vertraagd;
}

export function NieuwBericht() {
  const [open, setOpen] = useState(false);
  const houder = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const klik = (e: MouseEvent) => {
      if (houder.current && !houder.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", toets);
    document.addEventListener("mousedown", klik);
    return () => {
      document.removeEventListener("keydown", toets);
      document.removeEventListener("mousedown", klik);
    };
  }, [open]);

  return (
    <div className="v6-nieuwbericht" ref={houder}>
      <button
        type="button"
        className="v6-knop nieuwknop"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icoon naam="plus" klein />
        Nieuw bericht
      </button>
      {open && <Paneel onKlaar={() => setOpen(false)} />}
    </div>
  );
}

function Paneel({ onKlaar }: { onKlaar: () => void }) {
  const [zoek, setZoek] = useState("");
  const [klantId, setKlantId] = useState<string | null>(null);
  const vraag = useVertraagd(zoek.trim());
  const nieuw = useNieuwBericht("vandaag");

  const klanten = useQuery({
    queryKey: ["v6", "nieuw-bericht", "klanten", vraag],
    queryFn: () => v6Api.zoekKlanten(vraag),
    enabled: vraag.length >= MIN_ZOEK && !klantId,
    staleTime: 30_000,
  });
  // Pas na de keuze: heeft deze klant een adres en een mobiel nummer?
  const klant = useQuery({
    queryKey: ["v6", "klant", klantId ?? ""],
    queryFn: () => v6Api.klant(klantId as string),
    enabled: Boolean(klantId),
  });

  const start = async (kanaal: NieuwKanaal) => {
    if (!klantId) return;
    try {
      await nieuw.start(klantId, kanaal);
      onKlaar();
    } catch {
      /* De fout staat in het paneel (nieuw.fout). */
    }
  };

  const k = klant.data;
  const mobiel = k?.mobiel ?? k?.telefoon ?? null;
  const rijen = klanten.data?.klanten ?? [];

  return (
    <div className="paneel" role="dialog" aria-label="Nieuw bericht">
      {!klantId ? (
        <>
          <label className="v6-zoek">
            <Icoon naam="zoek" klein />
            <input
              type="search"
              autoFocus
              placeholder="Klant op naam, e-mail of nummer"
              aria-label="Zoek een klant"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
            />
          </label>
          {vraag.length < MIN_ZOEK ? (
            <p className="uitleg">Typ minstens twee tekens.</p>
          ) : klanten.isPending ? (
            <p className="uitleg">Zoeken</p>
          ) : klanten.isError ? (
            <p className="v6-fout" role="alert">
              Zoeken lukte niet. Probeer het opnieuw.
            </p>
          ) : rijen.length === 0 ? (
            <p className="uitleg">Geen klant gevonden.</p>
          ) : (
            <ul className="klanten" aria-label="Gevonden klanten">
              {rijen.slice(0, 8).map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => setKlantId(r.id)}>
                    <b>{r.naam}</b>
                    <small>
                      {[r.woonplaats, r.aantal_orders ? `${r.aantal_orders} bestellingen` : null]
                        .filter(Boolean)
                        .join(", ")}
                    </small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <div className="gekozen">
            <b>{k?.naam ?? "Klant laden"}</b>
            <button type="button" className="terug" onClick={() => setKlantId(null)}>
              Andere klant
            </button>
          </div>
          {klant.isError ? (
            <p className="v6-fout" role="alert">
              De klant kon niet geladen worden.
            </p>
          ) : (
            <div className="kanalen" role="group" aria-label="Kanaal">
              <KanaalKnop
                kanaal="mail"
                woord="E-mail"
                adres={k?.email ?? null}
                reden="geen e-mailadres"
                bezig={!k || nieuw.bezig}
                onKies={start}
              />
              <KanaalKnop
                kanaal="whatsapp"
                woord="WhatsApp"
                adres={mobiel}
                reden="geen mobiel nummer"
                bezig={!k || nieuw.bezig}
                onKies={start}
              />
            </div>
          )}
          {nieuw.fout && (
            <p className="v6-fout" role="alert">
              Kon geen bericht klaarzetten: {nieuw.fout}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function KanaalKnop({
  kanaal,
  woord,
  adres,
  reden,
  bezig,
  onKies,
}: {
  kanaal: NieuwKanaal;
  woord: string;
  adres: string | null;
  reden: string;
  bezig: boolean;
  onKies: (kanaal: NieuwKanaal) => void;
}) {
  const uit = !adres;
  return (
    <button
      type="button"
      className="v6-knop kanaalknop"
      disabled={bezig || uit}
      title={uit ? reden : adres}
      onClick={() => onKies(kanaal)}
    >
      <Icoon naam={kanaal} klein />
      {woord}
      {uit && <small>{reden}</small>}
    </button>
  );
}
